import { RECENT_ENROLLMENTS_DAYS } from "@/lib/dashboard/recent-enrollments";
import { getActiveRoleRequestHeaders } from "@/lib/utils/api/active-role-header";
import { resolveOpenApiBasePath } from "@/lib/utils/api/openapi-configuration";
import { getEffectiveTenantApiHeaders } from "@/lib/utils/env/effective-tenant";
import type { EnrollmentStatus } from "@/openapi/generated";

export type RecentEnrollmentStatusChangeDto = {
  id: string;
  activityId: number;
  enrollmentId: number;
  studentId: number;
  studentName: string;
  classId: number;
  className: string;
  locationId: number | null;
  status: EnrollmentStatus;
  statusLabel: string;
  activityCategory: "enrollments" | "drops" | "waitlist";
  /** Admin or staff created the enrollment, or recorded the drop. */
  studioInitiated: boolean;
  /** When the change was recorded (UTC instant). */
  changedAt: string;
  /** Effective withdraw day (YYYY-MM-DD), when this row is a drop or scheduled withdraw. */
  dropDate: string | null;
};

export async function fetchRecentEnrollmentStatusChanges(
  accessToken: string,
  days = RECENT_ENROLLMENTS_DAYS,
): Promise<RecentEnrollmentStatusChangeDto[]> {
  const baseUrl = resolveOpenApiBasePath();
  const url = new URL(
    `${baseUrl}/api/class-enrollments/recent-status-changes`,
  );
  url.searchParams.set("days", String(days));

  const response = await fetch(url.toString(), {
    headers: {
      ...getEffectiveTenantApiHeaders(),
      ...getActiveRoleRequestHeaders(),
      Authorization: `Bearer ${accessToken}`,
    },
  });

  if (!response.ok) {
    const body = await response.text().catch(() => "");
    throw new Error(
      body || `Failed to load recent enrollment changes (${response.status})`,
    );
  }

  const data = (await response.json()) as unknown;
  if (!Array.isArray(data)) return [];

  return data.map((row) => normalizeRecentEnrollmentStatusChangeRow(row));
}

function readString(row: Record<string, unknown>, key: string): string {
  const value = row[key];
  return typeof value === "string" ? value : "";
}

function readNumber(row: Record<string, unknown>, key: string): number {
  const value = row[key];
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
}

function normalizeRecentEnrollmentStatusChangeRow(
  row: unknown,
): RecentEnrollmentStatusChangeDto {
  const record =
    row != null && typeof row === "object"
      ? (row as Record<string, unknown>)
      : {};

  const id = readString(record, "id");
  const activityId =
    readNumber(record, "activityId") ||
    (() => {
      const match = /^activity-(\d+)$/.exec(id);
      return match ? Number(match[1]) : 0;
    })();

  return {
    id,
    activityId,
    enrollmentId: readNumber(record, "enrollmentId"),
    studentId: readNumber(record, "studentId"),
    studentName: readString(record, "studentName"),
    classId: readNumber(record, "classId"),
    className: readString(record, "className"),
    locationId: (() => {
      const raw = record.locationId;
      if (raw == null) return null;
      const n = Number(raw);
      return Number.isFinite(n) && n > 0 ? n : null;
    })(),
    status: record.status as EnrollmentStatus,
    statusLabel: readString(record, "statusLabel"),
    activityCategory: (readString(record, "activityCategory") ||
      "enrollments") as RecentEnrollmentStatusChangeDto["activityCategory"],
    studioInitiated: record.studioInitiated === true,
    changedAt: readString(record, "changedAt"),
    dropDate: readOptionalYmd(record, "dropDate"),
  };
}

function readOptionalYmd(
  row: Record<string, unknown>,
  key: string,
): string | null {
  const value = row[key];
  if (typeof value !== "string") return null;
  const match = /^(\d{4}-\d{2}-\d{2})/.exec(value.trim());
  return match ? match[1] : null;
}
